import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, KeyRound, Trash2 } from "lucide-react";

import GlassContainer from "@/shared/components/glasscontainer";
import Button from "@/shared/components/button";
import { toast } from "@/shared/components/toast";

import { CopyBlock } from "./CopyBlock";
import PersonalAccessTokenService, {
  type PersonalAccessToken,
} from "../services/PersonalAccessTokenService";
import {
  MCP_ENDPOINT_STORAGE_KEY,
  buildClaudeCodeConfig,
  buildClaudeDesktopConfig,
  buildSmokeTestCurl,
  defaultMcpEndpoint,
  resolveMcpEndpoint,
} from "../lib/mcpConfig";

const tokenService = new PersonalAccessTokenService();

function Step({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <GlassContainer className="p-6">
      <div className="flex items-start gap-4">
        <span className="shrink-0 size-8 rounded-full bg-accent/15 text-accent font-bold flex items-center justify-center text-sm">
          {number}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-foreground">{title}</h3>
          {description && (
            <p className="text-sm text-foreground/50 mt-0.5 mb-4">{description}</p>
          )}
          <div className={description ? "" : "mt-4"}>{children}</div>
        </div>
      </div>
    </GlassContainer>
  );
}

export default function McpConfigPanel() {
  const [endpoint, setEndpoint] = useState(() => {
    // resolveMcpEndpoint también sanea valores viejos guardados como ruta relativa ("/mcp").
    const stored = localStorage.getItem(MCP_ENDPOINT_STORAGE_KEY);
    return stored ? resolveMcpEndpoint(stored) : defaultMcpEndpoint();
  });

  const [tokenName, setTokenName] = useState("Claude Code MCP");
  const [token, setToken] = useState("");
  const [creating, setCreating] = useState(false);
  const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);

  const persistEndpoint = (value: string) => {
    setEndpoint(value);
    localStorage.setItem(MCP_ENDPOINT_STORAGE_KEY, value);
  };

  const loadTokens = async () => {
    try {
      setTokens(await tokenService.list());
    } catch {
      // silencioso: la lista es informativa
    }
  };

  useEffect(() => {
    void loadTokens();
  }, []);

  const generate = async () => {
    if (!tokenName.trim()) {
      toast.warning("Ponle un nombre al token");
      return;
    }
    setCreating(true);
    try {
      const created = await tokenService.create(tokenName.trim());
      setToken(created.token);
      toast.success("Token generado. Cópialo ahora: no se vuelve a mostrar.");
      void loadTokens();
    } catch (e) {
      toast.error((e as Error).message || "No se pudo generar el token");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await tokenService.revoke(id);
      toast.success("Token revocado");
      void loadTokens();
    } catch (e) {
      toast.error((e as Error).message || "No se pudo revocar el token");
    }
  };

  const claudeCode = useMemo(
    () => buildClaudeCodeConfig({ endpoint, token }),
    [endpoint, token],
  );
  const claudeDesktop = useMemo(
    () => buildClaudeDesktopConfig({ endpoint, token }),
    [endpoint, token],
  );
  const smokeCurl = useMemo(
    () => buildSmokeTestCurl({ endpoint, token }),
    [endpoint, token],
  );

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-foreground/60">
        Conecta un cliente de IA (Claude Code / Claude Desktop) al servidor MCP de Nodric para
        operar la plataforma con lenguaje natural. Sigue los pasos y copia la configuración generada.
      </p>

      <Step
        number={1}
        title="Endpoint del servidor MCP"
        description="URL del servidor MCP (transporte Streamable HTTP). Se guarda en este navegador."
      >
        <input
          value={endpoint}
          onChange={(e) => persistEndpoint(e.target.value)}
          spellCheck={false}
          className="w-full rounded-xl border border-black/10 bg-white/60 px-4 py-2.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent transition-colors"
          placeholder="http://localhost:5199/mcp"
        />
      </Step>

      <Step
        number={2}
        title="Token de acceso personal"
        description="El MCP autentica con un token portador (Authorization: Bearer). Genera uno y cópialo: solo se muestra una vez."
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              spellCheck={false}
              className="flex-1 rounded-xl border border-black/10 bg-white/60 px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-accent transition-colors"
              placeholder="Nombre del token (p. ej. Claude Code MCP)"
            />
            <Button variant="primary" onClick={generate} disabled={creating}>
              <span className="flex items-center gap-2">
                <KeyRound className="size-4" />
                {creating ? "Generando…" : "Generar token"}
              </span>
            </Button>
          </div>

          {token && (
            <div>
              <CopyBlock label="Token generado (cópialo ahora)" code={token} />
              <div className="flex items-start gap-2 mt-2 text-xs text-amber-600">
                <AlertTriangle className="size-3.5 shrink-0 mt-0.5" />
                <span>
                  Trátalo como una contraseña. No se guarda ni se vuelve a mostrar: si lo pierdes,
                  revócalo y genera otro.
                </span>
              </div>
            </div>
          )}

          {tokens.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground/50 uppercase tracking-widest">
                Tokens existentes
              </p>
              {tokens.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-black/5 bg-white/40 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-foreground">{t.name}</span>
                    <span className="ml-2 font-mono text-xs text-foreground/40">
                      {t.prefix}…
                    </span>
                    {t.revokedAtUtc && (
                      <span className="ml-2 text-xs text-red-500">revocado</span>
                    )}
                  </div>
                  {!t.revokedAtUtc && (
                    <button
                      onClick={() => revoke(t.id)}
                      title="Revocar"
                      className="shrink-0 p-1.5 rounded-lg text-foreground/40 hover:text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Step>

      <Step
        number={3}
        title="Configuración generada"
        description="Copia el bloque según tu cliente. En Claude Code va en un archivo .mcp.json del proyecto."
      >
        <div className="space-y-5">
          <CopyBlock label="Claude Code — .mcp.json" code={claudeCode} />
          <CopyBlock label="Claude Desktop — claude_desktop_config.json" code={claudeDesktop} />
        </div>
      </Step>

      <Step
        number={4}
        title="Probar la conexión"
        description="Ejecuta este comando en tu terminal: una respuesta con el resultado de initialize confirma que la credencial y el endpoint funcionan."
      >
        <CopyBlock label="Smoke test (initialize)" code={smokeCurl} />
      </Step>

      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => {
            persistEndpoint(defaultMcpEndpoint());
            setToken("");
            toast.success("Configuración restablecida");
          }}
        >
          Restablecer
        </Button>
      </div>
    </div>
  );
}
